import type { JournalSource } from '@palaccount/contracts';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Ltr } from '@/components/amount';

/**
 * FIN-02 §5a FIELD 6 — THE SOURCE, ONCE, FOR BOTH SCREENS THAT DRAW IT.
 *
 * The field, verbatim from the spec (line 24153):
 *
 *   | 6 | Source | Calculated link | — | `INV-2042` | On an automatic entry, a
 *   button showing the source document's icon and number that **navigates to
 *   that document**. On a manual entry, a dashed grey chip reading **Manual**.
 *
 * and the rule it serves, `BR-FIN-02-12` (spec 24299): *"Every automatic entry
 * must carry a working link to the document that produced it."*
 *
 * ── WHY ONE COMPONENT AND NOT TWO RENDERINGS ───────────────────────────────
 *
 * The register and the editor both answer "what raised this entry?", and a
 * reader who sees one word on the list and a different word on the page has
 * been told the product disagrees with itself. This project has the scar:
 * D5's aged-debt buckets were ruled "five everywhere" precisely because "one
 * number must not disagree with itself one click away". So the cell is written
 * once and imported twice.
 *
 * ── FOUR STATES, AND EACH ONE IS A DIFFERENT SENTENCE ──────────────────────
 *
 *   1. A HAND-TYPED ENTRY — `source` null, `autoGenerated` false. The dashed
 *      "Manual" chip the field describes. Unchanged from before A15.
 *
 *   2. AN AUTOMATIC ENTRY WHOSE SOURCE NOBODY RECORDED — `source` null,
 *      `autoGenerated` true. Ruling D28's own word: "Not recorded" for
 *      pre-feature history, never "System" (which D28 reserves for deliberate
 *      system acts, and this is an unknown rather than an act). It can only
 *      ever mean an entry posted before 20260913100000 whose source no table
 *      pointed at — the seam refuses a new one.
 *
 *   3. A SOURCE WITH A PAGE — the link, and the ordinary case.
 *
 *   4. A SOURCE WITH NO PAGE — the name, and the number if there is one, as
 *      plain text. Two acts leave no row at all (ADM-03's opening-balance
 *      import; INV-06's manual `ADJ-M` movement over a split), and one kind
 *      has a row this build cannot address (see `routeForSource`). Drawing a
 *      dead link there would meet the letter of "a link" and break the word
 *      that matters in the rule, which is WORKING.
 *
 * State 2 and state 4 are deliberately different: "we do not know" and "there
 * is nothing to open" are not the same fact, and a screen that renders them
 * identically has thrown away the distinction the columns exist to keep.
 */
export function JournalSourceCell({
  source,
  autoGenerated,
}: {
  source: JournalSource;
  autoGenerated: boolean;
}) {
  const { t } = useTranslation();

  if (source === null) {
    return (
      <span className="rounded border border-dashed px-2 py-1 text-xs text-muted-foreground">
        {t(autoGenerated ? 'je.sourceNotRecorded' : 'je.typeManual')}
      </span>
    );
  }

  /*
   * A REVERSAL KEEPS ITS OWN SENTENCE, and this is the one branch that is
   * about prose rather than about data.
   *
   * `journal_entry` is a real source type — FIN-02 §5a names "on a reversal,
   * the Journal entry it reversed" as the fourth kind — and the ledger stamps
   * it like any other. But "Reversal of JE-0051" says more than "Journal entry
   * JE-0051": it names the RELATION, not just the kind, and the editor has
   * shown exactly that sentence since before A15. Rewording it to match the
   * other twenty-four would have been the wave inventing a change nobody
   * asked for, so instead the register gains the editor's sentence.
   *
   * The `code` test is not defensive noise. A reversal the 20260913100000
   * backfill described carries no code (a migration has no tenant context in
   * which to format a number), and "Reversal of " with nothing after it is
   * worse than the kind on its own — the same reasoning A23 applied to
   * "Reason: " with nothing after it.
   */
  const label =
    source.type === 'journal_entry' && source.code !== null
      ? t('je.reversalOfLink', { no: source.code })
      : null;

  const body =
    label !== null ? (
      label
    ) : (
      <>
        {t(`je.source.${source.type}`)}
        {source.code === null ? null : (
          <>
            {' '}
            <Ltr value={source.code} className="font-mono" />
          </>
        )}
      </>
    );

  const route = routeForSource(source);
  if (route === null) {
    return <span className="text-sm">{body}</span>;
  }
  return (
    <Link to={route} className="text-sm text-primary underline-offset-2 hover:underline">
      {body}
    </Link>
  );
}

/**
 * WHERE A SOURCE OPENS — `null` where this build has nowhere honest to send a
 * reader.
 *
 * The precedent is `features/search/result-routes.ts`, which faced the same
 * question for a search hit and answered it twice in the same file: an ACCOUNT
 * goes to `/accounting/chart` because *"FIN-01 is a TREE, not a row-per-page
 * screen… inventing one would be inventing a screen. The chart is where an
 * account is looked at, so the chart is where the row goes"*, and an EMPLOYEE
 * goes to `/employees` for the same reason. That rule is followed here: a kind
 * whose record is looked at on ONE unambiguous screen routes to that screen,
 * with or without an id.
 *
 * It is NOT followed where the screen would be a guess, and those cases return
 * null and render as text:
 *
 *   `check` — FIN-10 and FIN-11 are TWO registers, incoming and outgoing, and
 *     the source carries no direction. Half of every such link would open the
 *     wrong register, which is worse than opening none: a reader who lands on
 *     a list that does not contain their cheque concludes the cheque is gone.
 *     Closing this needs the direction on the entry or a per-cheque route, and
 *     both are somebody's decision rather than this wave's guess.
 *
 *   `pos_sale` — `/pos` is the till itself, outside the shell and gated on a
 *     different area; it is not where a past sale is looked at. See its arm.
 *
 *   `opening_balance_import` and `stock_movement` — never carry an id at all
 *     (the act leaves no row), so there is nothing for a path to name.
 *
 * The switch is exhaustive over `JournalSourceType` with no `default`, so
 * adding a source kind is a compile error here rather than a silent unlinked
 * cell — the property the closed enum in the contract exists to buy.
 */
export function routeForSource(source: NonNullable<JournalSource>): string | null {
  const id = source.id;
  switch (source.type) {
    // ── A page per document ────────────────────────────────────────────────
    case 'invoice':
      return id === null ? null : `/sales/${id}`;
    case 'credit_note':
      return id === null ? null : `/sales/returns/${id}`;
    case 'delivery_note':
      return id === null ? null : `/sales/deliveries/${id}`;
    case 'purchase_invoice':
      return id === null ? null : `/purchases/bills/${id}`;
    case 'purchase_return':
      return id === null ? null : `/purchases/returns/${id}`;
    case 'goods_receipt':
      return id === null ? null : `/purchases/receipts/${id}`;
    case 'import_order':
      return id === null ? null : `/purchases/imports/${id}`;
    case 'receipt':
      return id === null ? null : `/accounting/receipts/${id}`;
    case 'payment':
      return id === null ? null : `/accounting/payments/${id}`;
    case 'cash_transfer':
      return id === null ? null : `/banks/transfers/${id}`;
    case 'stock_adjustment':
      return id === null ? null : `/inventory/adjustments/${id}`;
    case 'stock_count':
      return id === null ? null : `/inventory/counts/${id}`;
    case 'stock_transfer':
      return id === null ? null : `/inventory/transfers/${id}`;
    case 'work_order':
      return id === null ? null : `/manufacturing/work-orders/${id}`;
    case 'journal_entry':
      return id === null ? null : `/accounting/journals/${id}`;

    // ── One screen where the record is looked at (the `account` precedent) ──
    case 'fixed_asset':
    case 'depreciation_run':
      // FIN-07 is one screen carrying both the register and its runs.
      return '/accounting/assets';
    case 'revaluation':
      // FIN-05 §5d — the revaluation lives on the currencies screen.
      return '/accounting/currencies';
    case 'payroll_run':
      return '/employees/payroll';
    case 'material_issue':
      return '/manufacturing/consumption';
    case 'bank_reconciliation':
      // FIN-09 is a TAB of the bank-accounts screen and the product already
      // deep-links it this way (`router.tsx` records the dashboard's
      // `?account=&tab=reconciliation` link). The account half is not on the
      // entry, so the tab opens on its own.
      return '/banks/accounts?tab=reconciliation';

    // ── Named, deliberately not linked ─────────────────────────────────────
    case 'pos_sale':
      /*
       * `/pos` is the TILL, not a sales history: a full-screen counter UI
       * deliberately OUTSIDE the shell — router.tsx's own words are "a counter
       * screen with a menu down the side is a counter screen a cashier can get
       * lost in, so the separation is structural" — and gated on the `sales`
       * area rather than accounting. Sending an accountant reading the journal
       * register into a cashier's till would leave the shell, demand a
       * permission they may not hold, and still not show the sale. That is the
       * "link that goes somewhere else" failure this whole rule is about, so
       * the sale is named and the link withheld until SAL-06 has a page for a
       * past sale.
       */
      return null;
    case 'drawer_deposit':
      /*
       * SAL-06 §12's "Drawer banked at close". The act HAS a row — the
       * `pos_shifts` shift — and this build has nowhere to open it: the
       * only POS route is `/pos`, and `pos_sale`'s arm above already gives
       * the whole argument for why that is not a destination (the till is
       * outside the shell, gated on `sales`, and is a counter screen rather
       * than a history). A closed shift has no page anywhere in the shell,
       * so there is nothing to link to that would not be worse than text.
       *
       * Named, not linked, therefore — and the id is still stamped on the
       * entry, so the day a shift gets a page this becomes a one-line
       * change rather than a backfill.
       */
      return null;
    case 'check':
    case 'opening_balance_import':
    case 'stock_movement':
      return null;
  }
}
